import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery, useInvalidateSupabase } from '@shared/supabase/hooks';
import { Skeleton, SectionErrorBoundary } from '@shared/ui';
import { captureError } from '@shared/lib/observability';

type EntityKind = 'brand' | 'advertiser';

interface AliasRow {
  id: string;
  alias: string;
  alias_type: string | null;
  brand_id?: string | null;
  advertiser_id?: string | null;
}
interface EntityRow { id: string; name: string | null }

const TABLE: Record<EntityKind, { alias: string; entity: string; fk: string }> = {
  brand:      { alias: 'brand_aliases',      entity: 'brands',      fk: 'brand_id' },
  advertiser: { alias: 'advertiser_aliases', entity: 'advertisers', fk: 'advertiser_id' },
};

// ig_handle removed: IG handles now live in brand_instagram, not brand_aliases (Task #3)
// Types must match add_brand_alias()'s allowed set (the matcher reads any alias type).
const ALIAS_TYPES = ['name', 'display_name', 'alt_name', 'hashtag', 'historical_note'];

export default function Aliases() {
  const { t } = useTranslation('admin');
  const invalidate = useInvalidateSupabase();
  const [params, setParams] = useSearchParams();
  const kind = (params.get('kind') as EntityKind) || 'brand';
  const typeFilter = params.get('tf') ?? '';
  const search = params.get('q') ?? '';
  const setParam = (key: string, val: string, isTab = false) => {
    const next = new URLSearchParams(params);
    if (val) next.set(key, val); else next.delete(key);
    setParams(next, { replace: !isTab });
  };
  const setKind = (k: EntityKind) => setParam('kind', k === 'brand' ? '' : k, true);
  const setTypeFilter = (v: string) => setParam('tf', v);
  const setSearch = (v: string) => setParam('q', v);
  const [entitySearch, setEntitySearch] = useState('');
  const [newAlias, setNewAlias] = useState('');
  const [newType, setNewType] = useState('name');
  const [newEntityId, setNewEntityId] = useState('');
  const [newEntityLabel, setNewEntityLabel] = useState('');
  const [busy, setBusy] = useState(false);

  const cfg = TABLE[kind];

  const { data, isLoading, error } = useSupabaseQuery<AliasRow[]>(
    ['admin-aliases', kind, typeFilter, search],
    async () => {
      let q: any = (supabase.from(cfg.alias) as any)
        .select(`id, alias, alias_type, ${cfg.fk}`)
        .order('alias', { ascending: true })
        .limit(500);
      if (typeFilter) q = q.eq('alias_type', typeFilter);
      if (search.trim()) q = q.ilike('alias', `%${search.trim()}%`);
      return q;
    },
  );

  const rows = data ?? [];

  // Entity name lookups for displayed aliases
  const entityIds = Array.from(new Set(rows.map((r) => (r as any)[cfg.fk]).filter(Boolean))) as string[];
  const { data: entityRows } = useSupabaseQuery<EntityRow[]>(
    ['admin-alias-entities', kind, entityIds.join(',')],
    async () => {
      if (!entityIds.length) return { data: [], error: null };
      return (supabase.from(cfg.entity) as any).select('id, name').in('id', entityIds);
    },
    { enabled: entityIds.length > 0 },
  );
  const entityName = new Map<string, string>();
  (entityRows ?? []).forEach((e) => entityName.set(e.id, e.name ?? e.id));

  // Entity search (for adding new alias)
  const { data: searchEntities } = useSupabaseQuery<EntityRow[]>(
    ['admin-alias-entity-search', kind, entitySearch],
    async () => {
      if (entitySearch.trim().length < 2) return { data: [], error: null };
      return (supabase.from(cfg.entity) as any)
        .select('id, name')
        .ilike('name', `%${entitySearch.trim()}%`)
        .limit(10);
    },
    { enabled: entitySearch.trim().length >= 2 },
  );

  const addAlias = async () => {
    if (!newAlias.trim() || !newEntityId) { alert(t('alias.needBoth')); return; }
    setBusy(true);
    let e: { message: string } | null = null;
    if (kind === 'brand') {
      // brand_aliases is locked down — writes only via the admin-gated SECURITY DEFINER RPC
      ({ error: e } = await (supabase as any).rpc('add_brand_alias', {
        p_brand_id: newEntityId, p_alias: newAlias.trim(), p_alias_type: newType, p_weight: 1,
      }));
    } else {
      const payload: Record<string, unknown> = {
        alias: newAlias.trim(),
        alias_type: newType,
        [cfg.fk]: newEntityId,
      };
      ({ error: e } = await (supabase.from(cfg.alias) as any).insert(payload));
    }
    setBusy(false);
    if (e) { captureError(e, { where: 'admin.aliases.add', table: cfg.alias, type: newType }); alert(`Add failed: ${e.message}`); return; }
    setNewAlias('');
    setEntitySearch('');
    setNewEntityId('');
    setNewEntityLabel('');
    invalidate(['admin-aliases']);
  };

  const removeAlias = async (row: AliasRow) => {
    if (!window.confirm(t('alias.confirmDelete', { alias: row.alias }))) return;
    let e: { message: string } | null = null;
    if (kind === 'brand') {
      ({ error: e } = await (supabase as any).rpc('delete_brand_alias', { p_id: row.id }));
    } else {
      ({ error: e } = await (supabase.from(cfg.alias) as any).delete().eq('id', row.id));
    }
    if (e) { captureError(e, { where: 'admin.aliases.remove', table: cfg.alias, id: row.id }); alert(`Delete failed: ${e.message}`); return; }
    invalidate(['admin-aliases']);
  };

  return (
    <SectionErrorBoundary name="admin-aliases">
      <h2 className="admin-page-title">{t('alias.title')}</h2>
      <p className="admin-page-sub">{t('alias.sub')}</p>

      <div className="admin-tabs">
        <button className={kind === 'brand' ? 'active' : ''} onClick={() => setKind('brand')}>
          {t('alias.tabBrand')}
        </button>
        <button className={kind === 'advertiser' ? 'active' : ''} onClick={() => setKind('advertiser')}>
          {t('alias.tabAdvertiser')}
        </button>
      </div>

      <div className="admin-panel">
        <div style={{ fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-semibold)', marginBottom: 10 }}>{t('alias.addNew')}</div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            placeholder={t('alias.aliasPlaceholder')}
            value={newAlias}
            onChange={(e) => setNewAlias(e.target.value)}
            style={{ flex: '1 1 180px', padding: '10px 12px', borderRadius: 10, background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border-strong)', fontSize: 'var(--fs-body)', outline: 'none' }}
          />
          <select
            value={newType}
            onChange={(e) => setNewType(e.target.value)}
            style={{ padding: '10px 12px', borderRadius: 10, background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border-strong)', fontSize: 'var(--fs-body)' }}
          >
            {ALIAS_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
          </select>
          <div style={{ position: 'relative', flex: '1 1 220px' }}>
            <input
              placeholder={kind === 'brand' ? t('alias.searchBrand') : t('alias.searchAdvertiser')}
              value={newEntityLabel || entitySearch}
              onChange={(e) => { setEntitySearch(e.target.value); setNewEntityId(''); setNewEntityLabel(''); }}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 10, background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border-strong)', fontSize: 'var(--fs-body)', outline: 'none' }}
            />
            {entitySearch && !newEntityId && (searchEntities ?? []).length > 0 && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 5, marginTop: 4,
                background: 'var(--bg-elev)', border: '1px solid var(--border-strong)',
                borderRadius: 10, maxHeight: 240, overflowY: 'auto',
              }}>
                {(searchEntities ?? []).map((ent) => (
                  <div
                    key={ent.id}
                    onClick={() => {
                      setNewEntityId(ent.id);
                      setNewEntityLabel(ent.name ?? ent.id);
                      setEntitySearch('');
                    }}
                    style={{ padding: '8px 12px', fontSize: 'var(--fs-body)', cursor: 'pointer', borderBottom: '1px solid var(--border)' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-soft)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    {ent.name ?? ent.id}
                  </div>
                ))}
              </div>
            )}
          </div>
          <button
            className="admin-btn-primary"
            onClick={addAlias}
            disabled={busy || !newAlias.trim() || !newEntityId}
          >
            {busy ? '…' : t('alias.add')}
          </button>
        </div>
      </div>

      <div className="admin-filters">
        <input
          className="search"
          placeholder={t('alias.searchAlias')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="">{t('alias.allTypes')}</option>
          {ALIAS_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
        </select>
      </div>

      {isLoading && <Skeleton height={280} />}
      {error && <div style={{ color: 'var(--danger)' }}>{String((error as Error)?.message ?? error)}</div>}

      {!isLoading && !error && (
        <div className="admin-table-wrap">
          {rows.length === 0 ? (
            <div className="admin-empty">{t('alias.empty')}</div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>{t('alias.colAlias')}</th>
                  <th>{t('alias.colType')}</th>
                  <th>{kind === 'brand' ? t('alias.colBrand') : t('alias.colAdvertiser')}</th>
                  <th style={{ width: 80 }}></th>
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 400).map((r) => {
                  const eid = (r as any)[cfg.fk] as string | null | undefined;
                  return (
                    <tr key={r.id}>
                      <td style={{ fontWeight: 'var(--fw-semibold)' }}>{r.alias}</td>
                      <td className="dim">
                        <span className="admin-pill kind-category">{r.alias_type ?? '—'}</span>
                      </td>
                      <td>{eid ? (entityName.get(eid) ?? eid.slice(0, 8)) : '—'}</td>
                      <td>
                        <button className="admin-btn-danger" onClick={() => removeAlias(r)}>
                          {t('alias.delete')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {rows.length > 400 && (
            <div style={{ padding: 10, color: 'var(--muted)', fontSize: 'var(--fs-small)', textAlign: 'center' }}>
              {t('alias.truncated', { shown: 400, total: rows.length })}
            </div>
          )}
        </div>
      )}
    </SectionErrorBoundary>
  );
}
